import React, { useState, useRef } from "react";
import {
  Box,
  Stack,
  IconButton,
  Typography,
  Menu,
  MenuItem,
  List,
  ListItem,
  ListItemButton,
  Divider,
  Tooltip,
} from "@mui/material";
import {
  Plus,
  MoreHorizontal,
  X,
  Check,
  Edit3,
  Trash2,
  Pin,
  PinOff,
  FileText,
} from "lucide-react";
import ConfirmationDialog from "../../dialogs/ConfirmationDialog";

const ContractAnalysisSidebar = ({
  sessions,
  activeSessionId,
  onSessionSelect,
  onNewAnalysis,
  onDeleteSession,
  onPinSession,
  onRenameSession,
  isMobile = false,
  onClose,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuSessionId, setMenuSessionId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState(null);
  const inputRef = useRef(null);

  const pinnedSessions = sessions.filter((s) => s.isPinned);
  const recentSessions = sessions.filter((s) => !s.isPinned);

  const handleOpenMenu = (e, sessionId) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
    setMenuSessionId(sessionId);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setMenuSessionId(null);
  };

  const handleStartEdit = () => {
    const session = sessions.find((s) => s.id === menuSessionId);
    if (session) {
      setEditTitle(session.title);
      setEditingId(menuSessionId);
    }
    handleCloseMenu();
  };

  const handleSaveEdit = (id) => {
    if (editTitle.trim()) {
      onRenameSession(id, editTitle.trim());
    }
    setEditingId(null);
    setEditTitle("");
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditTitle("");
  };

  const handleInputChange = (e) => {
    const cursorPosition = inputRef.current?.selectionStart;
    setEditTitle(e.target.value);
    setTimeout(() => {
      inputRef.current?.setSelectionRange(cursorPosition, cursorPosition);
    }, 0);
  };

  const handleTogglePin = () => {
    const session = sessions.find((s) => s.id === menuSessionId);
    if (session) {
      onPinSession(session.id, !session.isPinned);
    }
    handleCloseMenu();
  };

  const handleDeleteClick = () => {
    const session = sessions.find((s) => s.id === menuSessionId);
    if (session) {
      setSessionToDelete(session);
      setDeleteDialogOpen(true);
    }
    handleCloseMenu();
  };

  const handleConfirmDelete = () => {
    if (sessionToDelete) {
      onDeleteSession(sessionToDelete.id);
    }
    setDeleteDialogOpen(false);
    setSessionToDelete(null);
  };

  const menuSession = sessions.find((s) => s.id === menuSessionId);

  const renderSessionItem = (session) => (
    <ListItem key={session.id} disablePadding sx={{ marginBottom: "4px" }}>
      <ListItemButton
        disableRipple
        selected={activeSessionId === session.id}
        onClick={() => onSessionSelect(session.id)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          height: "40px",
          padding: "8px 16px",
          borderRadius: "8px",
          backgroundColor: activeSessionId === session.id ? "#F0F9FF" : "transparent",
          border: "1px solid transparent",
          transition: "all 0.2s ease-in-out",
          marginBottom: "2px",
          "&:hover": {
            backgroundColor: activeSessionId === session.id ? "#E0F2FE" : "#F8FAFC",
          },
        }}
      >
        {editingId === session.id ? (
          <Box sx={{ display: "flex", alignItems: "center", width: "100%", gap: 1 }}>
            <input
              ref={inputRef}
              style={{
                width: "100%",
                fontSize: "14px",
                padding: "6px 12px",
                border: "1px solid #D1D5DB",
                borderRadius: "6px",
                fontFamily: "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                outline: "none",
              }}
              type="text"
              autoFocus
              value={editTitle}
              onClick={(e) => e.stopPropagation()}
              onFocus={(e) => e.stopPropagation()}
              onKeyPress={(e) => e.key === "Enter" && handleSaveEdit(session.id)}
              onChange={handleInputChange}
            />
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); handleSaveEdit(session.id); }}
              sx={{
                width: "28px", height: "28px", borderRadius: "6px",
                backgroundColor: "#DCFCE7", color: "#16A34A",
                "&:hover": { backgroundColor: "#BBF7D0" },
              }}
            >
              <Check size={16} />
            </IconButton>
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); handleCancelEdit(); }}
              sx={{
                width: "28px", height: "28px", borderRadius: "6px",
                backgroundColor: "#FEE2E2", color: "#DC2626",
                "&:hover": { backgroundColor: "#FECACA" },
              }}
            >
              <X size={16} />
            </IconButton>
          </Box>
        ) : (
          <>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: "500",
                  color: activeSessionId === session.id ? "#12243C" : "#475569",
                  fontFamily: "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  flex: 1,
                  lineHeight: "1.4",
                }}
              >
                {session.title}
              </Typography>
            </Box>
            {activeSessionId === session.id && (
              <IconButton
                size="small"
                onClick={(e) => handleOpenMenu(e, session.id)}
                sx={{
                  width: "28px", height: "28px", borderRadius: "6px",
                  color: "#64748B", backgroundColor: "transparent",
                  "&:hover": { backgroundColor: "#F1F5F9", color: "#12243C" },
                }}
              >
                <MoreHorizontal size={16} />
              </IconButton>
            )}
          </>
        )}
      </ListItemButton>
    </ListItem>
  );

  return (
    <Box
      sx={{
        display: isMobile ? "block" : { xs: "none", lg: "block" },
        width: isMobile ? "300px" : "280px",
        flexShrink: 0,
        height: "100%",
        boxSizing: "border-box",
        margin: isMobile ? "0" : "12px",
        backgroundColor: "#FFFFFF",
        borderRadius: isMobile ? "0" : "12px",
        border: isMobile ? "none" : "1px solid #F1F5F9",
        boxShadow: isMobile ? "none" : "0 1px 3px rgba(0, 0, 0, 0.04)",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          backgroundColor: "#FFFFFF",
          borderRadius: "12px",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            padding: "20px 24px",
            borderBottom: "1px solid #F1F5F9",
            backgroundColor: "#FFFFFF",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <Box
                sx={{
                  display: "flex", alignItems: "center", justifyContent: "center",
                  width: "40px", height: "40px",
                  backgroundColor: "#F5F3FF", borderRadius: "12px", color: "#6366f1",
                }}
              >
                <FileText size={20} />
              </Box>
              <Typography
                sx={{
                  fontSize: "18px", fontWeight: "600", color: "#12243C",
                  fontFamily: "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                }}
              >
                Contract History
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <Tooltip title="New Analysis" placement="top">
                <span>
                  <IconButton
                    onClick={onNewAnalysis}
                    disabled={!activeSessionId}
                    sx={{
                      width: "32px", height: "32px", borderRadius: "8px",
                      backgroundColor: "#F5F3FF", color: "#6366f1",
                      "&:hover": { backgroundColor: "#EDE9FE", transform: "scale(1.05)" },
                      "&:disabled": { backgroundColor: "#F1F5F9", color: "#94A3B8" },
                      transition: "all 0.2s ease-in-out",
                    }}
                  >
                    <Plus size={18} />
                  </IconButton>
                </span>
              </Tooltip>
              {onClose && (
                <IconButton
                  onClick={onClose}
                  sx={{
                    width: "32px", height: "32px", borderRadius: "8px",
                    color: "#64748B", backgroundColor: "#F8FAFC",
                    "&:hover": { backgroundColor: "#F1F5F9", color: "#12243C" },
                    transition: "all 0.2s ease-in-out",
                  }}
                >
                  <X size={18} />
                </IconButton>
              )}
            </Box>
          </Box>
        </Box>

        {/* Session list */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            padding: "16px 0",
            "::-webkit-scrollbar": { width: "6px" },
            "::-webkit-scrollbar-track": { background: "#F8FAFC" },
            "::-webkit-scrollbar-thumb": {
              background: "#CBD5E1", borderRadius: "8px",
              "&:hover": { background: "#94A3B8" },
            },
          }}
        >
          {sessions.length > 0 ? (
            <List sx={{ padding: "0 16px" }}>
              {[
                { items: pinnedSessions, label: "Pinned", showPin: true },
                { items: recentSessions, label: "Recent", showPin: false },
              ].map(
                (section, i) =>
                  section.items.length > 0 && (
                    <React.Fragment key={i}>
                      <Typography
                        sx={{
                          display: "flex", gap: "8px", alignItems: "center",
                          padding: "8px 16px 4px 16px",
                          color: "#64748B", fontWeight: "600", fontSize: "12px",
                          textTransform: "uppercase", letterSpacing: "0.5px",
                          fontFamily: "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                        }}
                      >
                        {section.label}
                        {section.showPin && <Pin size={12} />}
                      </Typography>
                      {section.items.map(renderSessionItem)}
                    </React.Fragment>
                  )
              )}
            </List>
          ) : (
            <Box
              sx={{
                display: "flex", flexDirection: "column",
                alignItems: "center", justifyContent: "center",
                padding: "40px 20px", textAlign: "center",
              }}
            >
              <Typography
                sx={{
                  fontSize: "14px", fontWeight: "500", color: "#64748B",
                  fontFamily: "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                  marginBottom: "8px",
                }}
              >
                No Contracts Yet
              </Typography>
              <Typography
                sx={{
                  fontSize: "13px", color: "#94A3B8",
                  fontFamily: "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                }}
              >
                Upload a contract to start your first analysis.
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Context menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        PaperProps={{
          sx: {
            borderRadius: "12px",
            boxShadow: "0 10px 25px rgba(0, 0, 0, 0.1)",
            border: "1px solid #E2E8F0",
            minWidth: "140px",
          },
        }}
      >
        <MenuItem
          onClick={handleTogglePin}
          sx={{
            fontSize: "14px",
            fontFamily: "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
            color: "#475569", padding: "8px 16px",
            "&:hover": { backgroundColor: "#F8FAFC" },
          }}
        >
          {menuSession?.isPinned ? (
            <><PinOff size={16} style={{ marginRight: "8px", color: "#64748B" }} />Unpin</>
          ) : (
            <><Pin size={16} style={{ marginRight: "8px", color: "#64748B" }} />Pin</>
          )}
        </MenuItem>
        <Divider sx={{ margin: "4px 0" }} />
        <MenuItem
          onClick={handleStartEdit}
          sx={{
            fontSize: "14px",
            fontFamily: "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
            color: "#475569", padding: "8px 16px",
            "&:hover": { backgroundColor: "#F8FAFC" },
          }}
        >
          <Edit3 size={16} style={{ marginRight: "8px", color: "#64748B" }} />
          Rename
        </MenuItem>
        <Divider sx={{ margin: "4px 0" }} />
        <MenuItem
          onClick={handleDeleteClick}
          sx={{
            fontSize: "14px",
            fontFamily: "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
            color: "#DC2626", padding: "8px 16px",
            "&:hover": { backgroundColor: "#FEF2F2" },
          }}
        >
          <Trash2 size={16} style={{ marginRight: "8px", color: "#DC2626" }} />
          Delete
        </MenuItem>
      </Menu>

      <ConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => { setDeleteDialogOpen(false); setSessionToDelete(null); }}
        title="Delete Contract"
        description={`This will permanently delete "${sessionToDelete?.title}". This action cannot be undone.`}
        onConfirm={handleConfirmDelete}
        confirmText="Delete"
        confirmColor="error"
      />
    </Box>
  );
};

export default ContractAnalysisSidebar;
